import useSyncUrlPathID from './hooks/useSyncUrlPathID'
import { CloseOutlined } from '@ant-design/icons'
import { Dropdown, Popover, Skeleton } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import './ChatHistory.css'
import { getIframesToken, IS_GUIDELINE_GPT, IS_IFRAMES, IS_MORTGAGE_GPT } from './constants'

// Custom three dots icon component
const ThreeDotsIcon = () => (
    <div className="three-dots-icon">
        <div className="dot"></div>
        <div className="dot"></div>
        <div className="dot"></div>
    </div>
)

// 去掉title开头的星号
export function filterTitle(title) {
    return title ? title.replace(/[#*]/g, '') : title;
}

// Title tooltip popover component
const TitleTooltip = ({ title, children, isMobile }) => {
    // Don't show popover on mobile
    if (isMobile) {
        return children
    }

    return (
        <Popover
            content={title}
            title={null}
            overlayClassName="chat-history-popover"
            styles={{
                body: {
                    display: 'inline-flex',
                    padding: '12px',
                    alignItems: 'center',
                    gap: '10px',
                    borderRadius: '8px',
                    background: 'rgba(0, 0, 0, 0.75)',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.12)',
                    border: 'none',
                    color: '#FFF',
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: '500',
                    lineHeight: '18px',
                },
            }}
            placement="left"
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0.1}
        >
            {children}
        </Popover>
    )
}

// More options dropdown component
const MoreDropdown = ({
    threadId,
    visible,
    onVisibleChange,
    onRename,
    onDelete,
    children,
}) => {
    const getMoreOptionsMenu = (threadId, onRename, onDelete) => ({
        items: [
            {
                key: 'rename',
                label: (
                    <div
                        className="more-option-item"
                        onClick={(e) => {
                            e.stopPropagation()
                            onRename(threadId)
                        }}
                    >
                        <span className="more-option-text">Rename</span>
                    </div>
                ),
            },
            {
                key: 'delete',
                label: (
                    <div
                        className="more-option-item delete"
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete(threadId)
                        }}
                    >
                        <span className="more-option-text delete">Delete</span>
                    </div>
                ),
            },
        ],
    })

    return (
        <Dropdown
            menu={getMoreOptionsMenu(threadId, onRename, onDelete)}
            trigger={['click']}
            open={visible}
            onOpenChange={onVisibleChange}
            overlayClassName="more-options-dropdown"
            placement="bottomRight"
            align={{
                offset: [0, 10],
            }}
        >
            {children}
        </Dropdown>
    )
}

// Chat history item component
const ChatHistoryItem = ({
    item,
    currentThreadID,
    hoveredItem,
    popoverVisible,
    editingItem,
    editableTitle,
    isMobile,
    onItemClick,
    onMouseEnter,
    onMouseLeave,
    onMoreClick,
    onRename,
    onDelete,
    onRenameChange,
    onRenameBlur,
    programs
}) => {
    const isActive = item.threadId === currentThreadID
    const isHovered = hoveredItem === item.threadId
    const isPopoverVisible = popoverVisible === item.threadId
    const isEditing = editingItem === item.threadId

    const handleMoreClick = (e) => {
        e.stopPropagation()
        onMoreClick(item.threadId)
    }

    const handleVisibleChange = (visible) => {
        if (!visible) {
            onMoreClick(null)
        }
    }

    const handleItemClick = () => {
        if (!isEditing) {
            onItemClick(item.threadId, item.type, item.documents)
        }
    }

    const description = useMemo(()=>{
        if(item.type === 'ScenarioDesk'){
            const LENGTH = item?.lenders?.length;
            return LENGTH ? `${LENGTH} Source${LENGTH > 1 ? 's' : ''} from ${item?.lenders?.join(', ')}` : 'Scenario AI' 
        } else {
            // 下划线去掉 换成空格
            return item.type?.replace?.(/_/g, " ") || "-"
        }
    }, [item]);

    const content = (
        <div
            className={`chat-history-title ${isActive ? 'chat-history-current' : ''}`}
            onMouseEnter={() => !isEditing && onMouseEnter(item.threadId)}
            onMouseLeave={onMouseLeave}
            onClick={handleItemClick}
        >
            <div className="history-item-content">
                <div className="history-item-main">
                    {isEditing ? (
                        <div className="history-item-editing">
                            <input
                                className="history-item-edit-input"
                                type="text"
                                value={editableTitle}
                                onChange={onRenameChange}
                                onBlur={onRenameBlur}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.target.blur() // Trigger blur event to save
                                    }
                                }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="history-item-title">
                                {filterTitle(item.title)}
                            </div>
                            <div className="history-item-type">
                                <img
                                    src={
                                        item.lenders?.length > 1
                                            ? '/images/ai-lenders.svg'
                                            : '/images/ai-lender.svg'
                                    }
                                    alt="Chat Icon"
                                />
                                <span>{description}</span>
                            </div>
                        </>
                    )}
                </div>
                {isHovered && !isEditing && !(IS_IFRAMES || IS_GUIDELINE_GPT) && (
                    <MoreDropdown
                        threadId={item.threadId}
                        visible={isPopoverVisible}
                        onVisibleChange={handleVisibleChange}
                        onRename={onRename}
                        onDelete={onDelete}
                    >
                        <div
                            className="history-item-more"
                            onClick={handleMoreClick}
                        >
                            <ThreeDotsIcon />
                        </div>
                    </MoreDropdown>
                )}
            </div>
        </div>
    )

    return isEditing ? (
        content
    ) : (
        <TitleTooltip open isMobile={isMobile} 
        title={
            <div className='history-item-title-desc'>
                <p>{filterTitle(item.title)}</p>
                <p>{description}</p>
            </div>
        }>
            {content}
        </TitleTooltip>
    )
}

export default React.forwardRef(function ChatHistory(props, ref) {
    const {
        getHistoryList,
        showHistory,
        setShowHistory,
        getList,
        threadID,
        isMobile,
        showPdf,
        onCloseMask,
        titleEmitter,
        programs
    } = props
    const { onRoutePathReplace } = useSyncUrlPathID();

    const [loading, setLoading] = useState(true)
    const [chatList, setChatList] = useState([])
    const [currentThreadID, setCurrentThreadId] = useState(threadID)
    const [hoveredItem, setHoveredItem] = useState(null)
    const [popoverVisible, setPopoverVisible] = useState(null)
    const [editingItem, setEditingItem] = useState(null)
    const [editableTitle, setEditableTitle] = useState('')
    const [originalTitle, setOriginalTitle] = useState('')

    const scrollContainerRef = React.useRef(null)

    React.useImperativeHandle(ref, () => ({
        refreshChatList: getChatList,
    }))

    const getChatList = useCallback(async () => {
        setLoading(true)
        const abortController = new AbortController()
        const token = IS_IFRAMES ? localStorage.getItem(getIframesToken()) : sessionStorage.getItem('ZeitroA')
        try {
            const response = await fetch('/los/mortgagegpt/chatlist', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    Cache: 'no-cache',
                },
                signal: abortController.signal,
            })

            if (!response.ok) {
                setLoading(false)
                throw new Error(`HTTP error! Status: ${response.status}`)
            }

            const data = await response.json()
            setLoading(false)
            setChatList(data.chat_list)
            getList(data.chat_list)
        } catch (error) {
            if (error.name !== 'AbortError') {
                setLoading(false)
                console.error('Fetch Error:', error)
            }
        }

        return () => abortController.abort()
    }, [])

    useEffect(() => {
        let cleanup = null

        const fetchData = async () => {
            cleanup = await getChatList()
        }

        fetchData()

        return () => {
            if (cleanup) {
                cleanup()
            }
        }
    }, [getChatList])

    const { MAP_GUIDELINE_ID } = programs.data;
    const groupedData = useMemo(() => {
        // Handle null/undefined chatList
        if (!chatList || !Array.isArray(chatList)) {
            return {}
        }
        
        return chatList.reduce((acc, item) => {
            const date = new Date(item[1])
            let group
            if (date.toLocaleDateString() === new Date().toLocaleDateString()) {
                group = 'Today'
            } else if (date > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) {
                group = 'Previous 7 days'
            } else {
                group = 'Past 30 days'
            }
            if (!acc[group]) {
                acc[group] = []
            }
            acc[group].push({
                threadId: item[0],
                date: date.toLocaleDateString(),
                titleFallback: item[2],
                title: item[3] || item[2],
                type: item[4] === '' ? 'FannieMae' : item[4],
                documents: item[5],
                lenders: [...new Set(item[5]?.map(k=>MAP_GUIDELINE_ID[k]?.lenderDisplayName)) || []]
            })
            return acc
        }, {})
    }, [chatList, MAP_GUIDELINE_ID])

    const entries = useMemo(() => Object.entries(groupedData), [groupedData])

    const handleClick = (threadId, type) => {
        sessionStorage.setItem(
            "activeGPTmode",
            // 会话历史记录的 “会话类型”
            type === "ScenarioDesk" ? "scenario" : "guideline",
        );
        setCurrentThreadId(threadId)
        getHistoryList(threadId, type)
        setShowHistory(false)
        onRoutePathReplace(threadId);
        onCloseMask && onCloseMask();
    }

    const handleMoreClick = (threadId) => {
        // Don't show popover if any item is being edited or on mobile
        if (editingItem || isMobile) return
        setPopoverVisible(popoverVisible === threadId ? null : threadId)
    }

    const handleRename = useCallback(
        (threadId) => {
            // Find the current title for this thread
            const currentItem = chatList.find((item) => item[0] === threadId)
            const currentTitle = currentItem
                ? currentItem[3] || currentItem[2]
                : ''

            setOriginalTitle(currentTitle)
            setEditableTitle(currentTitle)
            setEditingItem(threadId)
            setPopoverVisible(null)
            setHoveredItem(null) // Clear hover state when entering edit mode
        },
        [chatList]
    )

    const handleRenameBlur = useCallback(async () => {
        if (!editingItem) return

        setEditingItem(null)
        setHoveredItem(null) // Clear hover state when exiting edit mode

        const token = sessionStorage.getItem('ZeitroA')
        try {
            const response = await fetch('/los/mortgagegpt/customchattitle', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    Cache: 'no-cache',
                },
                body: JSON.stringify({
                    ThreadID: editingItem,
                    ChatTitle: editableTitle,
                }),
            })

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`)
            }

            // Save current scroll position
            const scrollTop = scrollContainerRef.current?.scrollTop || 0

            // Refresh the chat list to get updated title
            await getChatList()

            // If edited chat is current chat, send event to update header title
            if (editingItem === currentThreadID && titleEmitter) {
                titleEmitter.emit({
                    threadId: editingItem,
                    newTitle: editableTitle,
                })
            }

            // Restore scroll position after the DOM updates
            setTimeout(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = scrollTop
                }
            }, 0)
        } catch (error) {
            console.error('Rename failed:', error)
            // Restore original title on error
            setEditableTitle(originalTitle)
        }
    }, [
        editingItem,
        editableTitle,
        originalTitle,
        getChatList,
        currentThreadID,
        titleEmitter,
    ])

    const handleRenameChange = useCallback((event) => {
        setEditableTitle(event.target.value)
    }, [])

    const handleDelete = useCallback(
        async (threadId) => {
            // Save current scroll position
            const scrollTop = scrollContainerRef.current?.scrollTop || 0

            const token = sessionStorage.getItem('ZeitroA')
            const body = { ThreadID: threadId }
            try {
                const response = await fetch('/los/mortgagegpt/hidechat', {
                    method: 'POST',
                    body: JSON.stringify(body),
                    headers: {
                        Authorization: 'Bearer ' + token,
                        Cache: 'no-cache',
                    },
                })

                if (response.status !== 200) {
                    console.error(
                        'Looks like there was a problem. Status Code: ' +
                            response.status
                    )
                    return
                }

                // Refresh the chat list after successful deletion
                await getChatList()

                // Restore scroll position after the DOM updates
                setTimeout(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTop = scrollTop
                    }
                }, 0)

                // Close the popover
                setPopoverVisible(null)

                // If the deleted thread is currently active, redirect to main page
                if (threadId === currentThreadID) {
                    window.location.href = '/app/mortgagegpt'
                }
            } catch (err) {
                console.error('Hide chat history failed:', err)
                setPopoverVisible(null)
            }
        },
        [getChatList, currentThreadID]
    )

    const handleMouseLeave = () => {
        setHoveredItem(null)
        setPopoverVisible(null)
    }

    const handleMouseEnter = (threadId) => {
        // Don't show hover state if any item is being edited or on mobile
        if (editingItem || isMobile) return
        setHoveredItem(threadId)
    }

    return (
        <div className={`chat-history ${(IS_IFRAMES || IS_GUIDELINE_GPT) ? 'iframe-chat-history-adjuster' : ''}`}>
            {loading ? (
                <div style={{ padding: '28px 16px' }}>
                    <Skeleton active paragraph={{ rows: 10 }} />
                </div>
            ) : (
                <>
                    {/* Header */}
                    {(isMobile && IS_MORTGAGE_GPT()) ? (
                        <div className="chat-history-header">
                            <div className="history-title">Chat history</div>
                            <CloseOutlined
                                style={{
                                    fontSize: '15.56px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setShowHistory(!showHistory)}
                            />
                        </div>
                    ) : (
                        (IS_IFRAMES || IS_GUIDELINE_GPT) ? null : <div className="chat-history-header" style={{ marginTop: showPdf ? 16 : 11}}>
                            <div
                                className="guideline-history-menu-btn"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setShowHistory(!showHistory)}
                            >
                                <img style={{width:21.6, height:19}} src="/images/guideline-history-menu.svg" alt="Close" />
                            </div>
                        </div>
                    )}

                    {/* History list */}
                    {chatList.length === 0 ? (
                        <div className="no-chat-text">
                            <svg xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '8px' }} width="32" height="32" viewBox="0 0 32 32" fill="none">
                                <path d="M24 5.33301C25.0609 5.33301 26.0783 5.75444 26.8284 6.50458C27.5786 7.25473 28 8.27214 28 9.33301V19.9997C28 21.0605 27.5786 22.078 26.8284 22.8281C26.0783 23.5782 25.0609 23.9997 24 23.9997H12.0133C11.5671 23.9997 11.1281 24.1116 10.7364 24.3253L7.51158 26.0843C5.92925 26.9474 4 25.8021 4 23.9997V9.33301C4 8.27214 4.42143 7.25473 5.17157 6.50458C5.92172 5.75444 6.93913 5.33301 8 5.33301H24Z" stroke="#8C8C8C" stroke-width="2" stroke-miterlimit="2.99574" stroke-linecap="square" stroke-linejoin="bevel" stroke-dasharray="2 4 2 4"/>
                            </svg>
                            <div>You don't have any chats yet. Select a guideline to
                            start your conversation.</div>
                        </div>
                    ) : (
                        <div
                            className="text-left chat-history-content"
                            ref={scrollContainerRef}
                        >
                            {entries.map(([group, items], groupIndex) => (
                                <div
                                    key={group}
                                    style={{
                                        marginBottom:
                                            groupIndex < entries.length - 1
                                                ? '24px'
                                                : '0',
                                    }}
                                >
                                    <div className="group-title">{group}</div>
                                    {items.map((item, index) => (
                                        <ChatHistoryItem
                                            key={index}
                                            item={item}
                                            programs={programs}
                                            currentThreadID={currentThreadID}
                                            hoveredItem={hoveredItem}
                                            popoverVisible={popoverVisible}
                                            editingItem={editingItem}
                                            editableTitle={editableTitle}
                                            isMobile={isMobile}
                                            onItemClick={handleClick}
                                            onMouseEnter={handleMouseEnter}
                                            onMouseLeave={handleMouseLeave}
                                            onMoreClick={handleMoreClick}
                                            onRename={handleRename}
                                            onDelete={handleDelete}
                                            onRenameChange={handleRenameChange}
                                            onRenameBlur={handleRenameBlur}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    )
})
